import * as XLSX from 'xlsx';

// Helper function to get backend data URL
const getDataUrl = (path) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
  const url = `${API_BASE}/data/${path}`;
  console.log('🔗 Building data URL:', url);
  return url;
};

export const readInventoryFile = async (platform) => {
  const dataUrl = getDataUrl(`inventory/${platform}.xlsx`);
  
  console.log(`=== ATTEMPTING TO READ ${platform} FILE ===`);
  console.log(`Data URL: ${dataUrl}`);
  
  try {
    console.log(`Step 1: Fetching ${platform} file from backend...`);
    console.log(`Step 1.5: Using URL: ${dataUrl}`);
    const response = await fetch(dataUrl);
    console.log(`Step 2: Fetch response status: ${response.status}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    console.log(`Step 3: Converting to array buffer...`);
    const arrayBuffer = await response.arrayBuffer();
    console.log(`Step 4: Array buffer size: ${arrayBuffer.byteLength} bytes`);
    
    console.log(`Step 5: Reading workbook for ${platform}...`);
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: 'array' });
    console.log(`Step 6: Workbook loaded, sheet names:`, workbook.SheetNames);
    
    const sheetName = workbook.SheetNames[0];
    console.log(`Step 7: Using sheet: ${sheetName}`);
    
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
    console.log(`Step 8: Data parsed, ${jsonData.length} rows`);
    
    if (jsonData.length > 0) {
      console.log(`Step 9: Sample row from ${platform}:`, JSON.stringify(jsonData[0], null, 2));
      console.log(`Step 10: Column names:`, Object.keys(jsonData[0]));
    }
    
    console.log(`=== SUCCESS: Loaded ${jsonData.length} items for ${platform} ===`);
    return jsonData;
  } catch (error) {
    console.error(`=== ERROR READING ${platform} FILE ===`);
    console.error(`Data URL attempted: ${dataUrl}`);
    console.error(`Error type: ${error.name}`);
    console.error(`Error message: ${error.message}`);
    console.error(`Full error:`, error);
    
    if (error.message.includes('404')) {
      console.error(`FILE NOT FOUND: ${dataUrl}`);
      console.error(`Make sure ${platform}.xlsx exists in backend/public/data/inventory/`);
    }
    
    return [];
  }
};

export const readMultiplePlatforms = async (platforms) => {
  console.log(`=== STARTING MULTI-PLATFORM READ ===`);
  console.log(`Platforms to process:`, platforms);
  
  const allResults = [];
  const errors = [];
  
  for (let i = 0; i < platforms.length; i++) {
    const platform = platforms[i];
    console.log(`\n--- Processing platform ${i + 1}/${platforms.length}: ${platform} ---`);
    
    try {
      const rawData = await readInventoryFile(platform);
      
      if (rawData.length === 0) {
        console.warn(`No data returned for ${platform}`);
        errors.push(`${platform}: No data returned`);
        continue;
      }
      
      console.log(`Raw data loaded for ${platform}:`, rawData.length, 'rows');
      const standardizedData = standardizeInventoryData(rawData);
      console.log(`Standardized data for ${platform}:`, standardizedData.length, 'rows');
      
      // Add platform identifier to each item
      const platformData = standardizedData.map(item => ({
        ...item,
        analysisplatform: platform
      }));
      
      allResults.push(...platformData);
      console.log(`✓ Successfully added ${platformData.length} items from ${platform}`);
      console.log(`Running total: ${allResults.length} items`);
      
    } catch (error) {
      console.error(`✗ Error processing ${platform}:`, error);
      errors.push(`${platform}: ${error.message}`);
    }
  }
  
  console.log(`\n=== MULTI-PLATFORM READ COMPLETE ===`);
  console.log(`Total platforms processed: ${platforms.length}`);
  console.log(`Total items loaded: ${allResults.length}`);
  console.log(`Errors encountered: ${errors.length}`);
  
  if (errors.length > 0) {
    console.error(`Platforms with errors:`, errors);
  }
  
  return allResults;
};

export const getAvailablePlatforms = () => {
  // All platforms with files in /public/data/inventory/
  return ['ASH', 'ATL', 'BNA', 'CAE', 'GSO', 'MEM', 'PNS', 'RDU', 'SAV', 'TYS'];
};

export const standardizeInventoryData = (rawData) => {
  console.log(`=== STANDARDIZING DATA ===`);
  console.log(`Input data length: ${rawData.length}`);
  
  if (rawData.length === 0) {
    console.warn('No raw data to standardize');
    return [];
  }
  
  console.log(`Sample raw item:`, JSON.stringify(rawData[0], null, 2));
  console.log(`Available columns:`, Object.keys(rawData[0]));
  
  // Filter out total rows and invalid data
  const filteredData = rawData.filter((item, index) => {
    const platform = item['Platform'];
    const sku = item['Generic/Single'];
    
    // Skip rows where Platform is 'Total' or contains filter information
    if (!platform || 
        platform.toString().toLowerCase().includes('total') ||
        platform.toString().toLowerCase().includes('applied filters') ||
        platform.toString().toLowerCase().includes('segment ops')) {
      console.log(`Filtering out row ${index}: Platform = "${platform}"`);
      return false;
    }
    
    // Skip rows without valid SKU
    if (!sku || sku === '') {
      console.log(`Filtering out row ${index}: No SKU`);
      return false;
    }
    
    return true;
  });
  
  console.log(`After filtering: ${filteredData.length} rows (removed ${rawData.length - filteredData.length} invalid rows)`);
  
  const standardized = filteredData.map((item, index) => {
    // Debug first few items
    if (index < 3) {
      console.log(`Standardizing item ${index}:`, {
        'Platform': item['Platform'],
        'Generic/Single': item['Generic/Single'],
        'Description': item['Description'],
        'OH Units': item['OH Units'],
        'Units Sold': item['Units Sold'],
        'Class': item['Class']
      });
    }
    
    // Calculate actual cost per unit from OH $ ÷ OH Units
    const ohUnits = parseInt(item['OH Units'] || 0);
    const ohDollars = parseFloat(item['OH $'] || 0);
    const actualCost = ohUnits > 0 ? ohDollars / ohUnits : 0;
    const oldAur = parseFloat(item['AUR'] || 0);
    
    const result = {
      sku: item['Generic/Single'] || '',
      description: item['Description'] || '',
      platform: item['Platform'] || '',
      site: item['Site'] || '',
      onHand: ohUnits,
      unitsSold: parseInt(item['Units Sold'] || 0),
      ohValue: ohDollars,
      sales: parseFloat(item['Sales'] || 0),
      actualCost: Number(actualCost.toFixed(4)), // Use actual cost instead of AUR
      oldAur: Number(oldAur.toFixed(2)), // Keep AUR for comparison
      class: item['Class'] || '',
      subclass: item['Subclass'] || '',
      color: item['Color'] || ''
    };
    
    // Debug first standardized item with cost calculation
    if (index === 0) {
      console.log(`=== COST CALCULATION EXAMPLE ===`);
      console.log(`OH Units: ${ohUnits}, OH $: ${ohDollars}, Old AUR: ${oldAur}`);
      console.log(`Actual Cost: ${ohDollars} ÷ ${ohUnits} = ${actualCost.toFixed(4)}`);
      console.log(`Sample standardized item:`, result);
      console.log(`SKU: "${result.sku}", OnHand: ${result.onHand}, Cost: $${result.actualCost}, Old AUR: $${result.oldAur}`);
    }
    
    return result;
  });
  
  console.log(`Final standardized data length: ${standardized.length}`);
  console.log(`=== STANDARDIZATION COMPLETE ===`);
  
  return standardized;
};